import type { ButtonHTMLAttributes, MouseEvent } from 'react';
import { X } from 'lucide-react';
import { clsx } from '../clsx';
import { Icon } from './Icon';
import styles from './FilterChip.module.css';

export interface FilterChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  label: string;
  /** Counter shown after the label (formatted by the caller, e.g. "2.418"). */
  count?: string | number | undefined;
  active?: boolean | undefined;
  /** `outline` is the lifecycle filter of lists (6/12, 12 px); `dark` the compact filter of the pipeline (5/10, 11 px, navy when active). */
  variant?: 'outline' | 'dark' | undefined;
  /** Shows a remove cross; the chip itself stays a toggle. */
  onRemove?: ((event: MouseEvent<HTMLButtonElement>) => void) | undefined;
}

/** Filter chip with counter and active state ("Alle 2.418", "Neu 214"; pipeline: "alle", "> 14 T"). */
export function FilterChip({
  label,
  count,
  active = false,
  variant = 'outline',
  onRemove,
  className,
  type = 'button',
  ...rest
}: FilterChipProps) {
  return (
    <span className={styles.wrap}>
      <button
        type={type}
        aria-pressed={active}
        className={clsx(
          'pressable',
          styles.chip,
          variant === 'dark' ? styles.dark : styles.outline,
          active && styles.active,
          onRemove && styles.removable,
          className,
        )}
        {...rest}
      >
        {label}
        {count !== undefined && count !== '' ? <span className={styles.count}>{count}</span> : null}
      </button>
      {onRemove ? (
        <button
          type="button"
          aria-label={`${label} entfernen`}
          className={clsx(styles.remove, variant === 'dark' && active && styles.removeOnDark)}
          onClick={onRemove}
        >
          <Icon icon={X} size={16} />
        </button>
      ) : null}
    </span>
  );
}
